// Enhanced error logging and handling for messaging system
export interface MessagingErrorDetails {
  code: string
  message: string
  details?: any
  context?: {
    userId?: string
    businessId?: string
    threadId?: string
    action?: string
  }
  timestamp: string
  stackTrace?: string
}

export class MessagingErrorHandler {
  private static logError(error: MessagingErrorDetails) {
    // Enhanced console logging with structured data
    console.group(`🚨 [MESSAGING ERROR] ${error.code}`)
    console.error('Message:', error.message)
    console.error('Timestamp:', error.timestamp)
    if (error.context) {
      console.error('Context:', error.context)
    }
    if (error.details) {
      console.error('Details:', error.details)
    }
    if (error.stackTrace) {
      console.error('Stack Trace:', error.stackTrace)
    }
    console.groupEnd()

    // Store error for potential reporting
    this.storeErrorForReporting(error)
  }

  private static storeErrorForReporting(error: MessagingErrorDetails) {
    try {
      const errorLog = localStorage.getItem('messagingErrors') || '[]'
      const errors = JSON.parse(errorLog)
      errors.push(error)
      
      // Keep only last 50 errors to prevent localStorage overflow
      if (errors.length > 50) {
        errors.splice(0, errors.length - 50)
      }
      
      localStorage.setItem('messagingErrors', JSON.stringify(errors))
    } catch (e) {
      console.warn('Failed to store messaging error for reporting:', e)
    }
  }

  public static logMessagingServiceError(
    error: any, 
    action: string, 
    context?: { userId?: string; businessId?: string; threadId?: string }
  ) {
    const errorDetails: MessagingErrorDetails = {
      code: error?.code || 'UNKNOWN_ERROR',
      message: error?.message || 'An unknown error occurred',
      details: error?.details || error,
      context: { action, ...context },
      timestamp: new Date().toISOString(),
      stackTrace: error?.stack || new Error().stack
    }

    this.logError(errorDetails)
    return errorDetails
  }

  public static logBusinessValidationError(
    businessId: string, 
    userId: string, 
    errorType: 'BUSINESS_NOT_FOUND' | 'BUSINESS_OWNER_NOT_FOUND' | 'CUSTOMER_NOT_FOUND',
    originalError?: any
  ) {
    const errorDetails: MessagingErrorDetails = {
      code: errorType,
      message: this.getBusinessValidationMessage(errorType, businessId),
      details: originalError,
      context: { 
        action: 'CREATE_THREAD_WITH_BUSINESS',
        userId,
        businessId
      },
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack
    }

    this.logError(errorDetails)
    return errorDetails
  }

  private static getBusinessValidationMessage(errorType: string, businessId: string): string {
    switch (errorType) {
      case 'BUSINESS_NOT_FOUND':
        return `Business with ID ${businessId} not found in businesses table or user_profiles table`
      case 'BUSINESS_OWNER_NOT_FOUND':
        return `Business owner not found in user_profiles for business ${businessId}`
      case 'CUSTOMER_NOT_FOUND':
        return `Customer not found in user_profiles`
      default:
        return `Unknown business validation error for business ${businessId}`
    }
  }

  public static getStoredErrors(): MessagingErrorDetails[] {
    try {
      const errorLog = localStorage.getItem('messagingErrors') || '[]'
      return JSON.parse(errorLog)
    } catch (e) {
      console.warn('Failed to retrieve stored messaging errors:', e)
      return []
    }
  }

  public static clearStoredErrors() {
    try {
      localStorage.removeItem('messagingErrors')
    } catch (e) {
      console.warn('Failed to clear stored messaging errors:', e)
    }
  }

  public static generateErrorReport(): string {
    const errors = this.getStoredErrors()
    const errorSummary = errors.reduce((acc, error) => {
      acc[error.code] = (acc[error.code] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return `
=== MESSAGING SYSTEM ERROR REPORT ===
Generated: ${new Date().toISOString()}
Total Errors: ${errors.length}

Error Summary:
${Object.entries(errorSummary).map(([code, count]) => `  ${code}: ${count}`).join('\n')}

Recent Errors (Last 10):
${errors.slice(-10).map(error => `
  ${error.timestamp} - ${error.code}
  Message: ${error.message}
  Context: ${JSON.stringify(error.context)}
`).join('\n')}

=== END REPORT ===
    `.trim()
  }
}

// Export for console debugging
(window as any).MessagingErrorHandler = MessagingErrorHandler